// T-197 (v1.8.70, Session 47, 13.10.2026; Kundin 12.10. "Umsatz ohne RG" und 13.10. "wir müssen das natürlich ändern"):
// Bankabgleich erkennt Gutscheinzahlungen, Rückfragen und Hinweise der Seite Bankabgleich (reine Funktionen, Tests bankabgleich.test.ts).

export interface GutscheinVorschlag {
  voucherId: number;
  code: string;
  invoiceNumber?: string | null;
  buyerName: string;
  amount: number;
  status: string;
  statusText?: string;
  confirmable: boolean;
}

export interface RechnungVorschlag {
  invoiceId: number;
  invoiceNumber: string;
  customerName: string;
  amountDue: number;
}

const euro = (n: number): string =>
  (n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

/** Kurzanzeige eines Gutscheins: Rechnung (oder Code), Käufer, Wert. */
export const gutscheinAnzeige = (g: GutscheinVorschlag): string => {
  const teile = [g.invoiceNumber ? `Rechnung ${g.invoiceNumber}` : `Code ${g.code}`];
  if (g.buyerName && g.buyerName.trim()) teile.push(g.buyerName.trim());
  teile.push(euro(g.amount));
  return teile.join(', ');
};

/** Rückfrage vor "Gutschein-Zahlung bestätigen": sagt, was passiert. */
export const gutscheinRueckfrage = (betrag: number, g: GutscheinVorschlag, manuell: boolean): string =>
  `Zahlung über ${euro(betrag)} für den Gutschein (${gutscheinAnzeige(g)})${manuell ? ' MANUELL zugeordnet' : ''} bestätigen?\n\n` +
  'Damit wird der Gutschein freigeschaltet, die Gutscheinrechnung auf bezahlt gesetzt und der Käufer erhält sofort die Mail mit dem Gutschein-PDF und der Rechnung.';

/** Rückfrage vor der Verbuchung auf eine Buchungsrechnung (wie bisher). */
export const rechnungRueckfrage = (betrag: number, r: RechnungVorschlag, manuell: boolean): string =>
  `Zahlung über ${euro(betrag)} auf Rechnung ${r.invoiceNumber} (${r.customerName || 'Kunde unbekannt'})${manuell ? ' MANUELL zugeordnet' : ''} verbuchen?`;

/** Erste Rückfrage nach einem Schutzhinweis (409). */
export const schutzRueckfrage = (meldung: string): string => `${meldung}\n\nTrotzdem verbuchen?`;

/** Ob nach "Trotzdem" eine zweite, ausdrückliche Bestätigung der Überzahlung nötig ist. */
export const ueberzahlungDabei = (code?: string | null, codes?: string[] | null): boolean =>
  code === 'Overpayment' || code === 'OverpaymentConfirmRequired' || (codes || []).includes('Overpayment');

/** Zweite Rückfrage vor einer Überzahlung (Text vom Server, sonst Standardtext). */
export const zweiteRueckfrage = (meldung?: string | null): string =>
  (meldung && meldung.includes('ZWEITE RÜCKFRAGE')
    ? meldung
    : 'ZWEITE RÜCKFRAGE, ÜBERZAHLUNG: Die Rechnung wäre danach mit mehr als dem Rechnungsbetrag bezahlt. Nur bestätigen, wenn der Gast tatsächlich zu viel gezahlt hat; die Differenz ist dann zu erstatten.') +
  '\n\nÜberzahlung wirklich verbuchen?';

/** Wert der manuellen Auswahl: "r:<Rechnungs-Id>" oder "g:<Gutschein-Id>". */
export const auswahlWert = (art: 'r' | 'g', id: number): string => `${art}:${id}`;

export const parseAuswahl = (wert?: string | null): { art: 'r' | 'g'; id: number } | null => {
  const m = /^([rg]):(\d+)$/.exec(wert || '');
  return m ? { art: m[1] as 'r' | 'g', id: parseInt(m[2], 10) } : null;
};

/**
 * Hinweis für eine noch offene Zeile, deren Rechnung bzw. Gutschein in dieser Sitzung schon über eine andere Zeile bezahlt wurde
 * (Fehlbuchung 12.10.2026: zwei Zeilen über 118,00 EUR schlugen dieselbe Rechnung vor).
 */
export const zeilenHinweis = (
  rechnungId: number | null | undefined,
  gutscheinId: number | null | undefined,
  bezahlteRechnungen: Set<number>,
  bestaetigteGutscheine: Set<number>
): string | null => {
  if (rechnungId != null && bezahlteRechnungen.has(rechnungId))
    return 'Diese Rechnung ist in dieser Sitzung bereits bezahlt worden. Gehört die Zahlung wirklich dazu? Bitte Verwendungszweck prüfen.';
  if (gutscheinId != null && bestaetigteGutscheine.has(gutscheinId))
    return 'Dieser Gutschein wurde in dieser Sitzung bereits bestätigt. Bitte prüfen (mögliche Doppelzahlung).';
  return null;
};
